import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAllRebuses, deleteRebus, duplicateRebus } from '../../../services/rebusService';
import { deleteRebusSession } from '../../../services/rebusSessionService';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../services/firebase';

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 6) return 'God natt';
  if (hour < 12) return 'God morgon';
  if (hour < 18) return 'God ettermiddag';
  return 'God kveld';
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('nn-NO', { day: 'numeric', month: 'short', year: 'numeric' });
}

const PREVIEW_COUNT = 3;

export default function RebusDashboard({ onCreateNew, onStartSession, onEditRebus, onOpenSession }) {
  const [rebuses, setRebuses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [sessionFilter, setSessionFilter] = useState('alle');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const rebusList = await getAllRebuses();
      setRebuses(rebusList);
    } catch (error) {
      console.error('Error loading rebuses:', error);
    }
    try {
      const sessionsSnap = await getDocs(collection(db, 'rebusSessions'));
      const sessionList = [];
      sessionsSnap.forEach(doc => sessionList.push({ id: doc.id, ...doc.data() }));
      setSessions(sessionList);
    } catch (error) {
      console.error('Error loading sessions:', error);
    }
    setLoading(false);
  };

  const handleCopyRebus = async (rebus) => {
    try {
      await duplicateRebus(rebus.id);
      await loadData();
    } catch (err) {
      alert('Klarte ikkje kopiere Mikkelchase: ' + err.message);
    }
  };

  const handleDeleteRebus = async (rebus) => {
    const confirmed = window.confirm(`Er du sikker på at du vil slette "${rebus.title}"? Dette kan ikkje angrast.`);
    if (!confirmed) return;

    try {
      await deleteRebus(rebus.id);
      setRebuses(rebuses.filter(r => r.id !== rebus.id));
    } catch (error) {
      console.error('Error deleting rebus:', error);
      alert('Kunne ikkje slette Mikkelchasen. Prøv igjen.');
    }
  };

  const handleDeleteSession = async (e, session) => {
    e.stopPropagation();
    const confirmed = window.confirm(`Slett økt med PIN ${session.id}? Dette kan ikkje angrast.`);
    if (!confirmed) return;

    try {
      await deleteRebusSession(session.id);
      setSessions(sessions.filter(s => s.id !== session.id));
    } catch (error) {
      console.error('Error deleting session:', error);
      alert('Kunne ikkje slette økta. Prøv igjen.');
    }
  };

  const filteredRebuses = rebuses.filter(rebus =>
    rebus.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const displayedRebuses = searchQuery || showAll
    ? filteredRebuses
    : filteredRebuses.slice(0, PREVIEW_COUNT);

  const hasMore = !searchQuery && filteredRebuses.length > PREVIEW_COUNT;

  const finishedCount = sessions.filter(s => s.status === 'finished').length;

  return (
    <div className="relative flex min-h-screen w-full flex-col bg-background-light font-display text-[#0d121b] pb-28 overflow-x-hidden">
      {/* Header */}
      <header className="sticky top-0 z-30 flex items-center bg-background-light/95 backdrop-blur-sm p-4 justify-between transition-colors duration-300">
        <div className="flex items-center gap-3">
          <Link to="/" className="flex size-12 shrink-0 items-center justify-center rounded-full bg-white p-1 shadow-ambient hover:shadow-ambient-lg transition-shadow" title="Tilbake til startsida">
            <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full size-10 flex items-center justify-center">
              <span className="material-symbols-outlined text-white text-xl">explore</span>
            </div>
          </Link>
          <div>
            <p className="text-sm font-medium text-gray-500 leading-none">{getGreeting()},</p>
            <h2 className="text-[#0d121b] text-xl font-bold leading-tight">Mikkelchase</h2>
          </div>
        </div>
      </header>

      {/* Search Bar */}
      <div className="px-4 py-2">
        <div className="group flex w-full flex-1 items-center rounded-2xl bg-white shadow-ambient ghost-border ghost-border-focus transition-all duration-200">
          <div className="text-emerald-600 flex items-center justify-center pl-4">
            <span className="material-symbols-outlined">search</span>
          </div>
          <input
            type="text"
            className="flex w-full min-w-0 flex-1 resize-none bg-transparent py-4 px-3 text-[#0d121b] placeholder:text-[#9ca3af] focus:outline-none border-none text-base font-medium"
            placeholder="Finn ein Mikkelchase..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Stats Section */}
      <div className="px-4 py-4">
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          {/* Rebus Count Card */}
          <div className="flex flex-col gap-1 rounded-2xl p-4 sm:p-5 bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-500/20 relative overflow-hidden group">
            <div className="absolute top-0 right-0 -mr-4 -mt-4 h-24 w-24 rounded-full bg-white/10 blur-xl group-hover:scale-150 transition-transform duration-500"></div>
            <div className="flex items-center gap-2 text-white/90">
              <span className="material-symbols-outlined text-[20px]">explore</span>
              <p className="text-sm font-medium leading-normal">Antal Mikkelchaser</p>
            </div>
            <p className="text-3xl font-extrabold leading-tight tracking-tight mt-2">
              {rebuses.length}
            </p>
          </div>

          {/* Finished Sessions Card */}
          <div className="flex flex-col gap-1 rounded-2xl p-4 sm:p-5 bg-white shadow-ambient relative overflow-hidden">
            <div className="flex items-center gap-2 text-emerald-600">
              <span className="material-symbols-outlined text-[20px]">check_circle</span>
              <p className="text-sm font-medium leading-normal">Gjennomforte</p>
            </div>
            <p className="text-[#0d121b] text-3xl font-extrabold leading-tight tracking-tight mt-2">
              {finishedCount}
            </p>
          </div>
        </div>
      </div>

      {/* Section Header */}
      <div className="flex items-center justify-between px-4 pt-2 pb-2">
        <h3 className="text-[#0d121b] text-xl font-bold leading-tight tracking-tight">Mine Mikkelchaser</h3>
        {hasMore && (
          <button
            onClick={() => setShowAll(!showAll)}
            className="text-emerald-600 text-sm font-bold hover:text-emerald-500 transition-colors"
          >
            {showAll ? 'Vis færre' : 'Vis alle'}
          </button>
        )}
      </div>

      {/* Rebus Feed */}
      <div className="flex flex-col gap-4 px-4 pb-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-500 font-medium">Lastar Mikkelchaser...</div>
          </div>
        ) : filteredRebuses.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <span className="material-symbols-outlined text-6xl text-gray-300 mb-4">explore</span>
            <h4 className="text-lg font-bold text-gray-600 mb-2">
              {searchQuery ? 'Fann ingen Mikkelchaser' : 'Ingen Mikkelchaser enno'}
            </h4>
            <p className="text-gray-500 mb-4">
              {searchQuery ? 'Prøv eit anna sokeord' : 'Lag din fyrste Mikkelchase for a kome i gang!'}
            </p>
            {!searchQuery && (
              <button
                onClick={onCreateNew}
                className="px-6 py-3 bg-gradient-to-r from-eq-primary to-eq-primary-container text-white rounded-xl font-bold hover:brightness-110 transition-all"
              >
                Lag Mikkelchase
              </button>
            )}
          </div>
        ) : (
          displayedRebuses.map((rebus) => (
            <RebusCard
              key={rebus.id}
              rebus={rebus}
              onStartSession={onStartSession}
              onEdit={onEditRebus}
              onDelete={handleDeleteRebus}
              onCopy={handleCopyRebus}
            />
          ))
        )}
      </div>

      {/* Active/Recent Sessions */}
      {sessions.length > 0 && (() => {
        const filteredSessions = sessions.filter(s => {
          if (sessionFilter === 'alle') return true;
          if (sessionFilter === 'aktiv') return s.status === 'active';
          if (sessionFilter === 'ventar') return s.status === 'waiting';
          if (sessionFilter === 'avslutta') return s.status === 'finished';
          return true;
        });

        const sessionFilterOptions = [
          { key: 'alle', label: 'Alle' },
          { key: 'aktiv', label: 'Aktiv' },
          { key: 'ventar', label: 'Ventar' },
          { key: 'avslutta', label: 'Avslutta' },
        ];

        return (
          <>
            <div className="flex items-center justify-between px-4 pt-4 pb-2">
              <h3 className="text-[#0d121b] text-xl font-bold leading-tight tracking-tight">Økter</h3>
            </div>

            {/* Status Filter Pills */}
            <div className="flex items-center gap-2 px-4 pb-3 overflow-x-auto">
              {sessionFilterOptions.map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setSessionFilter(opt.key)}
                  className={`px-4 py-1.5 rounded-full text-sm font-bold whitespace-nowrap transition-colors duration-200 ${
                    sessionFilter === opt.key
                      ? 'bg-emerald-600 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="flex flex-col gap-3 px-4 pb-4">
              {filteredSessions.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <span className="material-symbols-outlined text-4xl text-gray-300 mb-2">filter_list</span>
                  <p className="text-gray-500 text-sm">Ingen økter med status &laquo;{sessionFilterOptions.find(o => o.key === sessionFilter)?.label}&raquo;</p>
                </div>
              ) : (
                filteredSessions.map((session) => {
                  const rebus = rebuses.find(r => r.id === session.rebusId);
                  const rebusTitle = rebus?.title || 'Ukjend Mikkelchase';
                  const teamCount = session.teams ? Object.keys(session.teams).length : 0;
                  const statusLabel = session.status === 'waiting' ? 'Ventar' : session.status === 'active' ? 'Aktiv' : 'Avslutta';
                  const statusColor = session.status === 'active' ? 'bg-green-100 text-green-700' : session.status === 'waiting' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600';
                  const borderColor = session.status === 'active' ? 'border-l-emerald-500' : session.status === 'waiting' ? 'border-l-yellow-400' : 'border-l-gray-300';

                  // Count submissions across all teams
                  const teams = session.teams ? Object.values(session.teams) : [];
                  const totalSubmissions = teams.reduce((sum, team) => {
                    const results = team.extraTaskResults;
                    if (!results) return sum;
                    return sum + Object.keys(results).length;
                  }, 0);

                  // Total possible tasks (extraTasks count from the rebus * team count)
                  const totalExtraTasks = rebus?.extraTasks?.length || 0;
                  const totalPossible = totalExtraTasks * teamCount;

                  return (
                    <div
                      key={session.id}
                      onClick={() => onOpenSession?.(session.id)}
                      className={`bg-white rounded-2xl border-l-4 ${borderColor} shadow-ambient p-4 hover:shadow-lg transition-shadow text-left w-full cursor-pointer`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-bold text-[#0d121b] truncate flex-1">{rebusTitle}</h4>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-bold px-2 py-1 rounded-full ${statusColor}`}>{statusLabel}</span>
                          <button
                            onClick={(e) => handleDeleteSession(e, session)}
                            className="text-gray-400 hover:text-red-500 transition-colors p-1 rounded-lg hover:bg-red-50"
                            title="Slett økt"
                          >
                            <span className="material-symbols-outlined text-[18px]">delete</span>
                          </button>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <span className="material-symbols-outlined text-[16px]">tag</span>
                          <span>PIN: {session.id}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="material-symbols-outlined text-[16px]">groups</span>
                          <span>{teamCount} lag</span>
                        </div>
                        {totalPossible > 0 && (
                          <div className="flex items-center gap-1">
                            <span className="material-symbols-outlined text-[16px]">task_alt</span>
                            <span>{totalSubmissions}/{totalPossible} oppgåver levert</span>
                          </div>
                        )}
                        <span className="ml-auto">{formatDate(session.createdAt)}</span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        );
      })()}

      {/* Floating Action Button */}
      <div className="fixed bottom-6 right-6 z-40">
        <button
          onClick={onCreateNew}
          className="group flex items-center justify-center size-14 hover:w-auto hover:pl-5 hover:pr-6 rounded-full bg-gradient-to-r from-eq-primary to-eq-primary-container text-white shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40 hover:brightness-110 transition-all duration-300 ease-out overflow-hidden"
        >
          <span className="material-symbols-outlined text-[26px] shrink-0 group-hover:rotate-90 transition-transform duration-300">add</span>
          <span className="max-w-0 overflow-hidden opacity-0 group-hover:max-w-48 group-hover:opacity-100 group-hover:ml-2 transition-all duration-300 ease-out text-sm font-bold whitespace-nowrap">Lag ny Mikkelchase</span>
        </button>
      </div>
    </div>
  );
}

function RebusCard({ rebus, onStartSession, onEdit, onDelete, onCopy }) {
  const stationCount = rebus.stations?.length || 0;
  const extraTaskCount = rebus.extraTasks?.length || 0;

  return (
    <div className="bg-white rounded-2xl shadow-ambient overflow-hidden hover:shadow-lg transition-shadow duration-200">
      <div className="p-4">
        {/* Title & Meta */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h4 className="text-[#0d121b] text-lg font-bold truncate">{rebus.title}</h4>
            {rebus.description && (
              <p className="text-gray-500 text-sm mt-0.5 line-clamp-1">{rebus.description}</p>
            )}
          </div>
        </div>

        {/* Stats row */}
        <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
          <div className="flex items-center gap-1">
            <span className="material-symbols-outlined text-[16px]">pin_drop</span>
            <span>{stationCount} {stationCount === 1 ? 'stasjon' : 'stasjonar'}</span>
          </div>
          {extraTaskCount > 0 && (
            <div className="flex items-center gap-1">
              <span className="material-symbols-outlined text-[16px]">star</span>
              <span>{extraTaskCount} ekstraoppgåver</span>
            </div>
          )}
          <div className="flex items-center gap-1 ml-auto">
            <span className="material-symbols-outlined text-[16px]">schedule</span>
            <span>{formatDate(rebus.updatedAt)}</span>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => onStartSession(rebus.id)}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-eq-primary to-eq-primary-container hover:brightness-110 text-white rounded-xl font-bold text-sm transition-all"
          >
            <span className="material-symbols-outlined text-[18px]">login</span>
            Gå inn
          </button>
          <button
            onClick={() => onEdit(rebus)}
            className="flex items-center justify-center size-10 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
            title="Rediger"
          >
            <span className="material-symbols-outlined text-[18px]">edit</span>
          </button>
          {onCopy && (
            <button
              onClick={() => onCopy(rebus)}
              className="flex items-center justify-center size-10 rounded-xl bg-gray-100 hover:bg-emerald-50 text-gray-600 hover:text-emerald-600 transition-colors"
              title="Kopier"
            >
              <span className="material-symbols-outlined text-[18px]">content_copy</span>
            </button>
          )}
          <button
            onClick={() => onDelete(rebus)}
            className="flex items-center justify-center size-10 rounded-xl bg-gray-100 hover:bg-red-50 text-gray-600 hover:text-red-600 transition-colors"
            title="Slett"
          >
            <span className="material-symbols-outlined text-[18px]">delete</span>
          </button>
        </div>
      </div>
    </div>
  );
}
